// frontend/src/components/DocumentationViewer/DocumentationViewer.jsx

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Box, Tabs, Tab, CircularProgress, Typography, IconButton, TextField, InputAdornment, List, ListItem, ListItemButton, ListItemText, Alert, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next'; 
// --- IMPORTS FOR SYNTAX HIGHLIGHTING ---
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// You can choose other themes like 'dracula', 'atomDark', 'materialDark', etc.
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'; 
import './DocumentationViewer.css';

// --- CodeBlock with Copy Button ---
const CodeBlock = ({ node, inline, className, children, ...props }) => {
    const [isCopied, setIsCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');

    const handleCopy = () => {
        navigator.clipboard.writeText(codeString);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Revert back after 2 seconds
    };

    if (!inline && match) {
        return (
            <div className="code-block-wrapper">
                <button 
                    onClick={handleCopy} 
                    className={`code-copy-btn ${isCopied ? 'copied' : ''}`}
                    title="Copy code"
                >
                    {isCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </button>
                <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{ borderRadius: '4px' }}
                    {...props}
                >
                    {codeString}
                </SyntaxHighlighter>
            </div>
        );
    }
    return (
        <code className={className} {...props}>
            {children}
        </code>
    );
};

const folderOrder = ['business', 'user_guides', 'technical', 'reference', 'legal_and_privacy', 'organization'];

const DocumentationViewer = ({ isOpen, onClose, initialPath }) => {
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { t } = useTranslation();

    // If there is no user, or the user is a guest, hide the sidebar and tabs.
    const showFullDocs = user && !user.is_guest;

    const { data: content, isLoading } = useQuery({
        queryKey: ['documentation'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/documentation');
            if (!res.ok) throw new Error('Failed to fetch documentation');
            return res.json();
        },
        enabled: isOpen,     // Only fetch when modal is actually opened
        staleTime: Infinity, // Documentation doesn't change mid-session; cache it permanently
    });
    const [activeFolder, setActiveFolder] = useState(null);
    const [activeFile, setActiveFile] = useState(null);
    
    // --- SEARCH STATE ---
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const mainPanelRef = useRef(null);
    const tabsListRef = useRef(null); // Ref for the sidebar tabs container
    const hasInitializedRef = useRef(false);

    const sortedFolders = useMemo(() => {
        if (!content) return [];
        return Object.keys(content).sort((a, b) => {
            const indexA = folderOrder.indexOf(a);
            const indexB = folderOrder.indexOf(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }, [content]);

    // Reset initialization when modal closes so it opens correctly next time
    useEffect(() => {
        if (!isOpen) {
            hasInitializedRef.current = false;
        }
    }, [isOpen]);

    // --- CONSOLIDATED INITIALIZATION LOGIC ---
    useEffect(() => {
        if (!content || sortedFolders.length === 0 || !isOpen) return;
        
        // Prevent the effect from forcibly resetting the active tab every time it renders
        if (hasInitializedRef.current) return;

        let targetFolder = sortedFolders[0]; 
        let targetFile = 0;                  

        if (initialPath) {
            const [folder, filename] = initialPath.split('/');
            if (content[folder]) {
                const fileIndex = content[folder].findIndex(f => f.filename === filename);
                if (fileIndex !== -1) {
                    targetFolder = folder;
                    targetFile = fileIndex;
                } else {
                    console.warn(`File ${filename} not found in folder ${folder} in the database.`);
                }
            } else {
                console.warn(`Folder ${folder} not found in the database.`);
            }
        }

        setActiveFolder(targetFolder);
        setActiveFile(targetFile);
        
        hasInitializedRef.current = true;
    }, [content, initialPath, sortedFolders, isOpen]);
    
    // --- AUTO-SCROLL TO ACTIVE FILE IN SIDEBAR ---
    useEffect(() => {
        if (activeFile !== null && tabsListRef.current) {
            // The Tabs component renders buttons. We need to find the active one.
            // Material UI assigns `aria-selected="true"` to the active tab.
            const activeTabElement = tabsListRef.current.querySelector('[aria-selected="true"]');
            
            if (activeTabElement) {
                // Scroll the tab into view smoothly
                activeTabElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center', // Center it vertically in the list
                });
            }
        }
    }, [activeFile, activeFolder]); // Run when file or folder changes

    // --- SEARCH LOGIC ---
    useEffect(() => {
        if (!searchQuery) {
            setSearchResults([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const results = [];

        sortedFolders.forEach(folder => {
            content[folder].forEach((file, fileIndex) => {
                const contentLower = file.content.toLowerCase();
                const titleLower = file.filename.toLowerCase();

                if (contentLower.includes(query) || titleLower.includes(query)) {
                    results.push({
                        folder: folder,
                        fileIndex: fileIndex,
                        filename: file.filename,
                    });
                }
            });
        });
        setSearchResults(results);
    }, [searchQuery, content, sortedFolders]);

    if (!isOpen) {
        return null;
    }

    const handleFolderChange = (event, newValue) => {
        setSearchQuery(''); 
        setActiveFolder(newValue);
        if (content[newValue]?.length > 0) {
            setActiveFile(0);
        }
        mainPanelRef.current?.scrollTo(0, 0);
    };

    const handleFileChange = (event, newValue) => {
        setActiveFile(newValue);
        mainPanelRef.current?.scrollTo(0, 0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSearchResultClick = (result) => {
        setActiveFolder(result.folder);
        setActiveFile(result.fileIndex);
        setSearchQuery(''); 
        mainPanelRef.current?.scrollTo(0, 0);
    };
    
    const formatTabLabel = (label, isFolder = false) => {
        if (!label) return '';
        let formatted = label.replace(/_/g, ' ').replace(/\.md$/, '');
        formatted = formatted.replace(/\bai\b/gi, 'AI');
        if (isFolder) {
            return formatted.toUpperCase();
        }
        return formatted.replace(/\b\w/g, char => char.toUpperCase());
    };
    
    return ReactDOM.createPortal(
        <div className="doc-viewer-overlay" onClick={onClose}>
            <div className="doc-viewer-content" onClick={e => e.stopPropagation()}>
                <IconButton onClick={onClose} className="doc-viewer-close-btn">
                    <CloseIcon />
                </IconButton>
                <Typography variant="h5" component="h2" className="doc-viewer-title">
                    Help & Documentation
                </Typography>
                
                {isLoading && <Box className="doc-viewer-loader"><CircularProgress color="secondary" /></Box>}
                
                {content && !isLoading && activeFolder && (
                    <>
                        {showFullDocs && (
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="doc-viewer-folder-tabs">
                                <Tabs value={searchQuery ? false : activeFolder} onChange={handleFolderChange} variant="scrollable" scrollButtons="auto">
                                    {sortedFolders.map(folder => (
                                        <Tab key={folder} label={formatTabLabel(folder, true)} value={folder} />
                                    ))}
                                </Tabs>
                            </Box>
                        )}
                        <Box className="doc-viewer-body">
                            
                            {/* ONLY RENDER SIDEBAR IF LOGGED IN */}
                            {showFullDocs && (
                                <Box className="doc-viewer-sidebar">
                                    <Box className="doc-viewer-search-box">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Search ..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    {searchQuery ? (
                                        <List className="search-results-list">
                                            {searchResults.length > 0 ? searchResults.map((result) => (
                                                <ListItem key={`${result.folder}-${result.filename}`} disablePadding>
                                                    <ListItemButton onClick={() => handleSearchResultClick(result)}>
                                                        <ListItemText
                                                            primary={formatTabLabel(result.filename)}
                                                            secondary={formatTabLabel(result.folder, true)}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            )) : (
                                                <Typography className="no-search-results">No results found.</Typography>
                                            )}
                                        </List>
                                    ) : (
                                        <div ref={tabsListRef} style={{ flexGrow: 1, overflowY: 'auto' }}> 
                                            <Tabs
                                                orientation="vertical"
                                                variant="scrollable"
                                                value={activeFile}
                                                onChange={handleFileChange}
                                                sx={{ 
                                                    borderRight: 1, 
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                {content[activeFolder]?.map((file, index) => (
                                                    <Tab key={file.filename} label={formatTabLabel(file.filename)} value={index} />
                                                ))}
                                            </Tabs>
                                        </div>
                                    )}
                                </Box>
                            )}

                            {/* MAIN PANEL (Takes 100% width if sidebar is hidden) */}
                            <Box className="doc-viewer-main-panel" ref={mainPanelRef}>
                                
                                {!showFullDocs && (
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mb: 3, 
                                            alignItems: 'center', // Centers the icon and text vertically
                                            '& .MuiAlert-action': {
                                                pt: 0, // Removes default MUI top padding on the action area
                                                alignItems: 'center' // Centers the button vertically
                                            }
                                        }}
                                        action={
                                            <Button color="inherit" size="small" href="/login" sx={{ fontWeight: 'bold' }}>
                                                {t('documentationViewer.loginBtn')}
                                            </Button>
                                        }
                                    >
                                        <Trans i18nKey="documentationViewer.previewAlert">
                                            You are viewing a limited preview. <strong>Log in</strong> to access the full documentation library, search functionality, and developer guides.
                                        </Trans>
                                    </Alert>
                                )}

                                {activeFile !== null && content[activeFolder]?.[activeFile] && (
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            code: CodeBlock,
                                            // --- Intercept links to force downloads and style them ---
                                            a: ({ node, children, href, ...props }) => {
                                                const isNotebook = href && href.endsWith('.ipynb');
                                                if (isNotebook) {
                                                    return (
                                                        <a href={href} download className="doc-download-btn" {...props}>
                                                            <DownloadIcon fontSize="small" />
                                                            {children}
                                                        </a>
                                                    );
                                                }
                                                // For normal links, open in a new tab safely
                                                return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
                                            }
                                        }}
                                    >
                                        {content[activeFolder][activeFile].content}
                                    </ReactMarkdown>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

export default DocumentationViewer;